/**
 * Discovery context (read model), boundary parsing of the search query.
 * Pure: no I/O. Turns raw URL query values (all strings, all untrusted) into a
 * normalized SearchParams the SQL layer can consume. Shape + ranges only, the
 * read model enforces no business rules (02-context-map.md §3).
 *
 * The four documented filters (01-discovery.md "Discovery"): category,
 * proximity (H11), min price (H12), rating (H5). Plus a convenience keyword
 * over business/category name. Rating is a minimum-average threshold; a
 * business with no reviews still passes it (H19); that predicate lives in the
 * SQL, not here.
 */

/** Rating filter buckets (H19): minimum average. */
export const RATING_BUCKETS = [3, 4, 4.5] as const;

/** Price filter buckets (H12): the cheapest service is "from $X" ≤ this. */
export const PRICE_BUCKETS_CENTS = [2500, 5000, 10000] as const;

export const DEFAULT_RADIUS_M = 10000;
export const MIN_RADIUS_M = 500;
export const MAX_RADIUS_M = 50000;
export const KEYWORD_MAX = 80;

export interface GeoPoint {
  lat: number;
  lng: number;
}

export interface SearchParams {
  /** A category id to filter by, or null for all categories. */
  categoryId: string | null;
  /** Free-text keyword over business + category name, or null. */
  keyword: string | null;
  /** Cheapest-service ceiling in cents (H12), or null for any price. */
  maxPriceCents: number | null;
  /** Minimum average rating (one of RATING_BUCKETS), or null for any. */
  minRating: number | null;
  /** The reference point for proximity (H11), or null when no location. */
  point: GeoPoint | null;
  /**
   * Radius in metres, or null for **no radius**: with a point and no radius
   * the point only orders the list, nearest first, and filters nothing out
   * (H56). Only meaningful when point is set.
   */
  radiusM: number | null;
}

// Category ids are text slugs (catalog.categories.id), e.g. 'physiotherapist'
// or 'massage-therapist', NOT uuids. Validate shape only; the read model does
// not check existence (an unknown slug simply matches no business).
const CATEGORY_ID = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const CATEGORY_ID_MAX = 64;

/** A URL query value: absent, single, or repeated. */
type Raw = string | string[] | undefined;

function first(raw: Raw): string | undefined {
  return Array.isArray(raw) ? raw[0] : raw;
}

function toFloat(raw: Raw): number | null {
  const v = first(raw);
  if (v === undefined || v.trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function parseCategoryId(raw: Raw): string | null {
  const v = (first(raw) ?? "").trim();
  if (v.length === 0 || v.length > CATEGORY_ID_MAX) return null;
  return CATEGORY_ID.test(v) ? v : null;
}

function parseKeyword(raw: Raw): string | null {
  const collapsed = (first(raw) ?? "").trim().replace(/\s+/g, " ");
  if (collapsed.length === 0) return null;
  return collapsed.slice(0, KEYWORD_MAX);
}

function parseMaxPrice(raw: Raw): number | null {
  const n = toFloat(raw);
  if (n === null || !Number.isInteger(n) || n <= 0) return null;
  return n;
}

function parseMinRating(raw: Raw): number | null {
  const n = toFloat(raw);
  if (n === null) return null;
  return (RATING_BUCKETS as readonly number[]).includes(n) ? n : null;
}

function parsePoint(latRaw: Raw, lngRaw: Raw): GeoPoint | null {
  const lat = toFloat(latRaw);
  const lng = toFloat(lngRaw);
  if (lat === null || lng === null) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
}

/**
 * Absent or unreadable is **no radius** (H56), not the default one: the apps
 * list every clinic nearest-first, and a search that silently dropped the ones
 * 11 km away would show someone in Tyre an empty list. The web always sends the
 * radius it shows, so it keeps its menu's meaning.
 */
function parseRadius(raw: Raw): number | null {
  const n = toFloat(raw);
  if (n === null) return null;
  return Math.min(MAX_RADIUS_M, Math.max(MIN_RADIUS_M, Math.round(n)));
}

/**
 * Parse the raw search query. Every malformed value degrades to "no filter"
 * rather than an error, search is forgiving. A point requires BOTH lat and
 * lng; a lone coordinate is ignored (distance sort/filter simply off).
 */
export function parseSearchParams(raw: {
  category?: Raw;
  q?: Raw;
  maxPrice?: Raw;
  minRating?: Raw;
  lat?: Raw;
  lng?: Raw;
  radius?: Raw;
}): SearchParams {
  return {
    categoryId: parseCategoryId(raw.category),
    keyword: parseKeyword(raw.q),
    maxPriceCents: parseMaxPrice(raw.maxPrice),
    minRating: parseMinRating(raw.minRating),
    point: parsePoint(raw.lat, raw.lng),
    radiusM: parseRadius(raw.radius),
  };
}
